/**
 * Canonical plan/role semantics — ONE source of truth.
 *
 * Background: we kept shipping bugs like "Super Admin shows Enterprise plan
 * badge" or "Enterprise tier shows Upgrade button" because individual pages
 * each re-implemented the `user.plan` display logic. This module fixes that
 * — every plan-aware UI element should use these helpers / components.
 *
 * Rules baked in:
 *   • super_admin → "Platform Owner" (never a subscriber plan label).
 *   • enterprise  → top-tier subscriber, no upgrade target.
 *   • starter / growth → mid-tier subscribers, can upgrade.
 *   • Anyone at top tier sees "Manage Subscription", not "Upgrade".
 */

const TIER_ORDER = { starter: 1, growth: 2, enterprise: 3 };

export function getPlanContext(user) {
  const role = user?.role;
  const planRaw = (user?.plan || '').toLowerCase();
  const tierIndex = TIER_ORDER[planRaw] || 0;

  // Super Admin is the platform owner — NOT a subscriber.
  if (role === 'super_admin') {
    return {
      label: 'Platform Owner',
      shortLabel: 'Owner',
      tone: 'owner',
      isPlatformOwner: true,
      isTopTier: true,
      canUpgrade: false,
      canDowngrade: false,
      cta: { type: 'platform-admin', label: 'Platform Administration', to: '/admin' },
    };
  }

  if (planRaw === 'enterprise') {
    return {
      label: 'Enterprise',
      shortLabel: 'Enterprise',
      tone: 'top-tier',
      isPlatformOwner: false,
      isTopTier: true,
      canUpgrade: false,
      canDowngrade: true,
      cta: { type: 'manage', label: 'Manage Subscription', to: '/settings' },
    };
  }

  if (planRaw === 'growth') {
    return {
      label: 'Growth',
      shortLabel: 'Growth',
      tone: 'mid',
      isPlatformOwner: false,
      isTopTier: false,
      canUpgrade: true,
      canDowngrade: true,
      cta: { type: 'upgrade', label: 'Upgrade for More Credits', to: '/settings' },
    };
  }

  // starter (or unknown → treat as starter)
  return {
    label: 'Starter',
    shortLabel: 'Starter',
    tone: 'starter',
    isPlatformOwner: false,
    isTopTier: false,
    canUpgrade: true,
    canDowngrade: false,
    cta: { type: 'upgrade', label: 'Upgrade for More Credits', to: '/settings' },
  };
}

/**
 * Returns Tailwind classes for a Badge representing the plan/owner status.
 * Use with `<Badge className={getPlanBadgeClasses(user)}>{getPlanContext(user).label}</Badge>`.
 */
export function getPlanBadgeClasses(user) {
  const ctx = getPlanContext(user);
  switch (ctx.tone) {
    case 'owner':     return 'bg-amber-500/20 text-amber-300 border-amber-400/30';
    case 'top-tier':  return 'bg-purple-500/20 text-purple-200 border-purple-400/30';
    case 'mid':       return 'bg-blue-500/20 text-blue-200 border-blue-400/30';
    case 'starter':
    default:          return 'bg-gray-500/20 text-gray-200 border-gray-400/30';
  }
}

/**
 * DEV-MODE GUARD — warns in the browser console when a component reads
 * `user.plan` directly without going through `getPlanContext`. Call this
 * inside any component you suspect is at risk:
 *
 *     useEffect(() => { warnIfRawPlanUsage(user, 'MyComponent'); }, [user]);
 *
 * No-op in production builds.
 */
let _warnedComponents = new Set();
export function warnIfRawPlanUsage(user, componentName) {
  if (process.env.NODE_ENV === 'production') return;
  if (!user) return;
  if (user.role !== 'super_admin') return;
  if (!user.plan) return;
  const key = componentName + ':' + user.plan;
  if (_warnedComponents.has(key)) return;
  _warnedComponents.add(key);
  console.warn(
    `[plan-display] ${componentName} appears to display "${user.plan}" for a super_admin. ` +
    `Use getPlanContext(user) / <PlanBadge user={user} /> instead so the platform owner ` +
    `never sees a misleading subscriber plan label.`
  );
}
